/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  DrmType as ClientDrmType,
  DrmConfig,
  TransportEncodingType,
  VideoProviderConfiguration
} from "client-code/video-provider-configurations";
import {
  Storage,
  TVideoProviderConfigurationsGetParams
} from "@tivocorp/data-domain-lib";

import { DeviceModel, TokenRefreshMechanism } from "../../../../model/device";
import { Log, Diagnostics } from "../../../logging";
import {
  VideoProviderConfigurationLogger,
  VideoProviderConfigurationTrigger
} from "../../../logging/VideoProviderConfigurationLogger";
import { ISubscription } from "../../ISubscription";
import { SubscriptionModel } from "../../SubscriptionModel";
import { SDTState, VideoProviderState, DrmType } from "../../enums";
import { IDrmManager, IVpcDrmInfo } from "../../interfaces";
import { TScopedDomainTokenNotifier } from "../../types";
import { ScopedDomainTokenModel } from "./ScopedDomainTokenModel";
import { VideoProviderConfigModel } from "../../../videoProvider";
import { VideoProviderConfigurationInteractor } from "../../../interactor";
import { ApplicationFeatureArea } from "../../../enums";

export class DrmManager implements IDrmManager {
  private static oDrmManager: DrmManager | null = null;

  private mSDTModel: ScopedDomainTokenModel | null = null;

  private drmInfo: IVpcDrmInfo = {} as IVpcDrmInfo;

  private oITivoSubscription: ISubscription<VideoProviderState>;

  private drmErrorCount: number = 0;

  /**
   * Advanced by every getDRMInfoForProviderId(). A request that resumes from
   * an await under an older value was replaced by a newer tune's: it must
   * neither write into that tune's drmInfo nor notify on its behalf.
   */
  private mRequestId: number = 0;

  private readonly mVpcInteractor: VideoProviderConfigurationInteractor =
    new VideoProviderConfigurationInteractor(ApplicationFeatureArea.ALL);

  /**
   * In-flight network refetch, shared so concurrent setupDrm() calls for one
   * tune issue a single VPC request rather than one each.
   */
  private mFreshVpcFetch: Promise<VideoProviderConfiguration[]> | null = null;

  private readonly TAG: string = "DrmManager";

  private constructor() {
    this.setupSDT();
    this.oITivoSubscription = new SubscriptionModel<VideoProviderState>();
  }

  private setupSDT() {
    this.mSDTModel = ScopedDomainTokenModel.getInstance();
    this.mSDTModel
      .getSDTFetchListener()
      .subscribe(this.onTokenFetch.bind(this));
  }

  private log(
    level: "debug" | "info" | "warn" | "error",
    message: string,
    ...optionalParams: unknown[]
  ): void {
    Diagnostics[level](this.TAG, message, ...optionalParams);
    Log[level[0] as "d" | "i" | "w" | "e"](
      this.TAG,
      message,
      ...optionalParams
    );
    if (level === "error") {
      Diagnostics.error(this.TAG, "stacktrace:,", optionalParams[0]);
    }
  }

  private onTokenFetch(sdtNotifyObject: TScopedDomainTokenNotifier) {
    if (sdtNotifyObject.drmCustomDomain !== this.drmInfo.drmCustomDomain) {
      this.setupScheduleSDTRefresh();
      return;
    }
    if (
      sdtNotifyObject.sdtState === SDTState.SUCCESS ||
      sdtNotifyObject.sdtState === SDTState.REFRESH
    ) {
      this.drmInfo.token = sdtNotifyObject.token || "";
      this.oITivoSubscription.notify(
        sdtNotifyObject.sdtState === SDTState.REFRESH
          ? VideoProviderState.REFRESH
          : VideoProviderState.SUCCESS
      );
      this.setupScheduleSDTRefresh();
      this.drmErrorCount = 0;
    } else if (sdtNotifyObject.sdtState === SDTState.ERROR) {
      if (
        !this.drmInfo.token ||
        sdtNotifyObject.drmCustomDomain === this.drmInfo.drmCustomDomain
      ) {
        this.drmErrorCount += 1;
        this.oITivoSubscription.notify(VideoProviderState.ERROR);
      }
    }
  }

  /**
   * True when the entry names a TiVo DTS domain, meaning its license-request
   * credential is a scoped domain token rather than an inline identifier.
   *
   * Keyed on the field rather than on drmServerType: the schema documents
   * drmCustomDomain as the domain used "for TiVo DRM (aka mobi DRM) playback",
   * which covers mobiWidevine and mobiFairPlay alike. Gating on
   * drmServerType === MobiWidevine left mobiFairPlay with no token at all once
   * it was added to supportedDrmType.
   */
  private usesScopedDomainToken(drmConfig: Partial<DrmConfig>): boolean {
    return !!drmConfig.drmCustomDomain;
  }

  private isWidevineServerSuppliedDeviceIdentifierMissing(
    drmConfig: Partial<DrmConfig>
  ): boolean {
    return !!(
      drmConfig.drmServerType === ClientDrmType.Widevine &&
      (!drmConfig.serverSuppliedDeviceIdentifier ||
        drmConfig.serverSuppliedDeviceIdentifier === "")
    );
  }

  /**
   * No DTS domain and no inline identifier means there is nothing to put in
   * drmCustomHeader. Playback would still be attempted and fail at the license
   * server with an empty header, so treat it as a missing configuration here.
   */
  private isLicenseCredentialMissing(drmConfig: Partial<DrmConfig>): boolean {
    return (
      !this.usesScopedDomainToken(drmConfig) &&
      !drmConfig.serverSuppliedDeviceIdentifier
    );
  }

  private isFairplayCertURLMissing(drmConfig: Partial<DrmConfig>): boolean {
    return !!(
      drmConfig.drmServerType === ClientDrmType.MobiFairPlay &&
      (drmConfig.certUrl === null || drmConfig.certUrl === undefined)
    );
  }

  /**
   * Names of the DRM configuration fields DRM setup needs but the entry lacks.
   * Names only, never values: the values are license URLs and credentials.
   */
  private getMissingDrmFields(
    drmConfig: Partial<DrmConfig> | null | undefined
  ): string[] {
    if (!drmConfig) {
      return ["drmConfig"];
    }
    const missingFields: string[] = [];
    if (!drmConfig.drmServerType) missingFields.push("drmServerType");
    if (!drmConfig.drmServerUrl) missingFields.push("drmServerUrl");
    if (!drmConfig.drmCustomHeader) missingFields.push("drmCustomHeader");
    if (
      !drmConfig.transportEncodingType ||
      drmConfig.transportEncodingType.length === 0
    ) {
      missingFields.push("transportEncodingType");
    }
    if (this.isWidevineServerSuppliedDeviceIdentifierMissing(drmConfig)) {
      missingFields.push("serverSuppliedDeviceIdentifier");
    }
    if (this.isFairplayCertURLMissing(drmConfig)) {
      missingFields.push("certUrl");
    }
    if (this.isLicenseCredentialMissing(drmConfig)) {
      missingFields.push("licenseCredential");
    }
    return missingFields;
  }

  private async isDrmInfoMissing(
    providerId: string,
    drmConfig: Partial<DrmConfig> | null | undefined
  ): Promise<boolean> {
    const missingFields = this.getMissingDrmFields(drmConfig);
    if (missingFields.length > 0) {
      VideoProviderConfigurationLogger.logDrmConfigInvalid(
        providerId,
        drmConfig && drmConfig.drmServerType
          ? String(drmConfig.drmServerType)
          : undefined,
        missingFields
      );
      this.drmErrorCount += 1;
      this.oITivoSubscription.notify(VideoProviderState.ERROR);
      return true;
    }
    return false;
  }

  public static getInstance(): DrmManager {
    if (!DrmManager.oDrmManager) {
      DrmManager.oDrmManager = new DrmManager();
    }

    return DrmManager.oDrmManager;
  }

  public getDrmManagerListener(): ISubscription<VideoProviderState> {
    return this.oITivoSubscription;
  }

  /**
   * Request params for the VPC endpoint. Shared by the startup fetch and the
   * on-demand refetch in fetchFreshVpcObject(), which cannot rely on the
   * service remembering them: the interactor builds a fresh service per call.
   */
  private async buildVpcRequestParams(): Promise<TVideoProviderConfigurationsGetParams> {
    // Request only what the running browser can actually decrypt. Safari on
    // non-TV devices decrypts FairPlay via EME, so add MobiFairPlay there to
    // let the VPC service return a FairPlay config for it.
    const supportedDrmType: ClientDrmType[] = [
      ClientDrmType.MobiWidevine,
      ClientDrmType.Clear
    ];
    if (DeviceModel.isSafari() && !DeviceModel.isTvDevice()) {
      supportedDrmType.push(ClientDrmType.MobiFairPlay);
    }

    return {
      msoPartnerId: await DeviceModel.getMsoPartnerId(),
      supportedDrmType,
      supportedTransportEncodingType: [
        TransportEncodingType.DashFmp4TransportStream,
        TransportEncodingType.DashTransportStream,
        TransportEncodingType.HlsFmp4TransportStream,
        TransportEncodingType.HlsTransportStream
      ]
    };
  }

  public async fetchVideoProviderConfiguration(): Promise<void> {
    try {
      const vpcParams: TVideoProviderConfigurationsGetParams =
        await this.buildVpcRequestParams();

      await this.mVpcInteractor.fetchVideoProviderConfigurations(vpcParams);
      const vpConfigurationsData = (await Storage.getInstance().get(
        Storage.Keys.VIDEO_PROVIDER_CONFIGURATIONS_DATA
      )) as Record<string, VideoProviderConfiguration> | null;

      VideoProviderConfigurationLogger.logFetchResult(
        VideoProviderConfigurationTrigger.Startup,
        vpConfigurationsData
      );

      if (vpConfigurationsData === null || vpConfigurationsData === undefined) {
        return;
      }

      // Mirror the same entries into the synchronous registry so callers that
      // cannot await storage (e.g. Redux selectors) can still answer
      // provider-configuration questions. No extra fetch — same objects. The
      // registry keys them by their own providerPartnerId, so the stored
      // shape (an array today) does not have to be a keyed map.
      VideoProviderConfigModel.primeAll(vpConfigurationsData);

      const vpcDataArray = Object.values(vpConfigurationsData);

      // Process configurations sequentially to avoid race conditions
      const processVpcData = async (
        vpcData: VideoProviderConfiguration
      ): Promise<void> => {
        try {
          if (
            vpcData.drmConfig !== null &&
            vpcData.drmConfig !== undefined &&
            this.usesScopedDomainToken(vpcData.drmConfig) &&
            this.mSDTModel !== null
          ) {
            await this.mSDTModel.getToken(
              vpcData.drmConfig.drmCustomDomain || "",
              vpcData.drmConfig.drmCustomHeaderAuthScheme || ""
            );
          }
        } catch {
          // On api error ScopeDomainTokenModel will notify
          // error as SDTState which will trigger to retry mechanism accordingly
        }
      };

      // Use reduce to process configurations sequentially
      await vpcDataArray.reduce(async (previousPromise, vpcData) => {
        await previousPromise;
        return processVpcData(vpcData);
      }, Promise.resolve());
    } catch (error) {
      this.log("error", "Failed to load video provider configurations.", error);
      VideoProviderConfigurationLogger.logFetchFailed(
        VideoProviderConfigurationTrigger.Startup,
        error
      );
    }
  }

  /**
   * Resolve the VPC entry for `providerId`.
   *
   * The cached entry is used when present. When it is missing — the 24-hour
   * cache expired, or the cached snapshot does not hold this provider — a
   * fresh copy is fetched from the network. A failed resolve counts exactly
   * one DRM error so onDrmFail() keeps its full retry budget.
   *
   * @returns The entry, and whether it came from a fresh network fetch (the
   *   caller then also forces a fresh SDT token). A missing entry is the
   *   caller's to count and report, since only it knows whether the request
   *   is still current.
   */
  private async resolveVpcObject(
    providerId: string
  ): Promise<{ vpcObject?: VideoProviderConfiguration; isFresh: boolean }> {
    let vpcObject: VideoProviderConfiguration | undefined;
    let isFresh = false;

    try {
      const cached =
        await this.mVpcInteractor.fetchVPConfigurationsForVideoPlayer(
          providerId
        );
      vpcObject = this.findVpcForProvider(cached, providerId);
    } catch (error: unknown) {
      // Once the cache expires the service throws here: it refetches with the
      // params of a previous call, and the interactor's fresh service instance
      // has none. Treat it as a miss and refetch below with explicit params.
      this.log(
        "warn",
        `resolveVpcObject() - cached VPC unavailable for providerId: ${providerId}`,
        error
      );
      VideoProviderConfigurationLogger.logCacheUnavailable(providerId, error);
    }

    if (!vpcObject) {
      vpcObject = await this.fetchFreshVpcObject(providerId);
      isFresh = vpcObject !== undefined;
    }

    if (!vpcObject) {
      return { isFresh: false };
    }

    // Keep the synchronous mirror warm for providers that were absent from
    // the startup snapshot and are only fetched on demand here.
    VideoProviderConfigModel.upsert(vpcObject);
    return { vpcObject, isFresh };
  }

  /**
   * Drop the cached VPC snapshot and fetch it again with explicit params, so
   * the refetch does not depend on any earlier call on a service instance.
   * The full snapshot also re-primes the synchronous mirror.
   */
  private async fetchFreshVpcObject(
    providerId: string
  ): Promise<VideoProviderConfiguration | undefined> {
    try {
      if (this.mFreshVpcFetch === null) {
        this.mFreshVpcFetch = this.refetchVpcConfigurations().finally(() => {
          this.mFreshVpcFetch = null;
        });
      }
      const configs = await this.mFreshVpcFetch;
      const vpcObject = this.findVpcForProvider(configs, providerId);
      // An empty snapshot is already reported as empty_response by the fetch.
      const providerCount = (configs || []).length;
      if (vpcObject === undefined && providerCount > 0) {
        VideoProviderConfigurationLogger.logProviderNotFound(
          providerId,
          providerCount
        );
      }
      return vpcObject;
    } catch (error: unknown) {
      this.log(
        "error",
        `fetchFreshVpcObject() failed for providerId: ${providerId}`,
        error
      );
      VideoProviderConfigurationLogger.logFetchFailed(
        VideoProviderConfigurationTrigger.Playback,
        error
      );
      return undefined;
    }
  }

  private async refetchVpcConfigurations(): Promise<
    VideoProviderConfiguration[]
  > {
    await this.mVpcInteractor.clearCache();
    const configs = await this.mVpcInteractor.fetchVideoProviderConfigurations(
      await this.buildVpcRequestParams()
    );
    VideoProviderConfigurationLogger.logFetchResult(
      VideoProviderConfigurationTrigger.Playback,
      configs
    );
    VideoProviderConfigModel.primeAll(configs);
    return configs;
  }

  private findVpcForProvider(
    configs: VideoProviderConfiguration[] | null | undefined,
    providerId: string
  ): VideoProviderConfiguration | undefined {
    return (configs || []).find(
      (obj: VideoProviderConfiguration) => obj.providerPartnerId === providerId
    );
  }

  /** Maps the VPC entry onto `drmInfo`. No token acquisition. */
  private populateBaseDrmInfo(
    drmInfo: IVpcDrmInfo,
    vpcObject: VideoProviderConfiguration
  ): void {
    const drmConfig = vpcObject.drmConfig;

    if (drmConfig === null || drmConfig === undefined) {
      drmInfo.drmType = DrmType.Clear as DrmType;
      drmInfo.drmServerUrl = "";
      drmInfo.drmCustomHeader = "";
      drmInfo.fairplayCertificateUrl = "";
      drmInfo.transportEncodingType = "";
    } else {
      drmInfo.drmType = drmConfig.drmServerType as DrmType;
      drmInfo.drmServerUrl = drmConfig.drmServerUrl || "";
      drmInfo.drmCustomHeader = drmConfig.drmCustomHeader || "";
      // FairPlay cannot build an SPC without the application certificate, so
      // omitting this fails playback with Shaka 6015 SERVER_CERTIFICATE_REQUIRED.
      drmInfo.fairplayCertificateUrl = drmConfig.certUrl || "";
      const encodingTypes = drmConfig.transportEncodingType;
      drmInfo.transportEncodingType =
        encodingTypes !== null &&
        encodingTypes !== undefined &&
        encodingTypes.length > 0
          ? encodingTypes[0].toString()
          : "";
    }

    drmInfo.providerPartnerId = vpcObject.providerPartnerId || "";
  }

  /**
   * An entry naming a DTS domain takes a scoped domain token from the SDT
   * model; an entry without one carries its identifier inline and clears the
   * error count. See usesScopedDomainToken() for why this is keyed on the
   * fields present rather than on drmServerType.
   */
  private async applyDrmToken(
    drmInfo: IVpcDrmInfo,
    drmConfig: Partial<DrmConfig>,
    isForced?: boolean
  ): Promise<void> {
    const serverSuppliedIdentifier =
      drmConfig.serverSuppliedDeviceIdentifier || "";

    if (!this.usesScopedDomainToken(drmConfig)) {
      drmInfo.token = serverSuppliedIdentifier;
      this.drmErrorCount = 0;
      return;
    }

    const customDomain = drmConfig.drmCustomDomain || "";
    const authScheme = drmConfig.drmCustomHeaderAuthScheme || "";
    drmInfo.drmCustomDomain = customDomain;
    drmInfo.drmCustomHeaderAuthScheme = authScheme;

    const mobiDrmToken =
      this.mSDTModel !== null
        ? await this.mSDTModel.getToken(customDomain, authScheme, isForced)
        : undefined;
    const scopedDomainToken =
      mobiDrmToken !== null && mobiDrmToken !== undefined
        ? mobiDrmToken.token || ""
        : "";
    // An entry may carry both fields. Prefer the DTS token and keep the inline
    // identifier as a fallback so drmCustomHeader is never sent empty.
    drmInfo.token = scopedDomainToken || serverSuppliedIdentifier;
  }

  public async getDRMInfoForProviderId(
    providerId: string,
    isForced?: boolean
  ): Promise<IVpcDrmInfo> {
    const drmInfo = {} as IVpcDrmInfo;
    this.drmInfo = drmInfo;
    this.mRequestId += 1;
    const requestId: number = this.mRequestId;

    // A non-forced call is a new tune: give it the full retry budget instead
    // of inheriting failures from earlier tunes on this singleton. Forced
    // calls are onDrmFail()/onDrmEnforcementFail() retries and keep counting.
    if (!isForced) {
      this.drmErrorCount = 0;
    }

    const { vpcObject, isFresh } = await this.resolveVpcObject(providerId);
    if (requestId !== this.mRequestId) {
      this.log(
        "info",
        `getDRMInfoForProviderId() - superseded request for providerId: ${providerId}`
      );
      return drmInfo;
    }
    if (!vpcObject) {
      this.drmErrorCount += 1;
      this.oITivoSubscription.notify(VideoProviderState.ERROR);
      return drmInfo;
    }

    this.populateBaseDrmInfo(drmInfo, vpcObject);

    const drmConfig = vpcObject.drmConfig;
    if (!VideoProviderConfigModel.isTivoPlusProviderPartnerId(providerId)) {
      const isDrmInfoMissing = await this.isDrmInfoMissing(
        providerId,
        drmConfig
      );
      if (!isDrmInfoMissing && drmConfig !== null && drmConfig !== undefined) {
        await this.applyDrmToken(drmInfo, drmConfig, isForced || isFresh);
      }
    } else {
      this.oITivoSubscription.notify(VideoProviderState.SUCCESS);
    }

    return drmInfo;
  }

  public setupScheduleSDTRefresh(): void {
    const sdtModel = this.mSDTModel;
    if (sdtModel) {
      sdtModel
        .getTokenToBeRefreshed()
        .then((tokenWithlowestExpirationTime) => {
          if (tokenWithlowestExpirationTime) {
            TokenRefreshMechanism.getInstance().scheduleDomainTokenRefresh(
              tokenWithlowestExpirationTime
            );
          }
        })
        .catch((error: unknown) => {
          this.log("error", `Failed to get token to be refreshed ${error}`);
        });
    }
  }

  public getDRMConfig(): IVpcDrmInfo {
    return this.drmInfo;
  }

  public getDRMErrorCount(): Promise<number> {
    return Promise.resolve(this.drmErrorCount);
  }
}
